// src/pages/RoleManagement/components/ApiPermissionTree.tsx
// Cây (jsTree-style) cho popup "Phân quyền API": cấp cha là Controller, cấp con là từng API
// permission cụ thể. Chọn cha -> tự chọn hết con; chọn đủ hết con -> tự tích cha (đúng hành vi
// checkbox cha-con chuẩn của jsTree).

import React, { useMemo, useState } from 'react';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import { ApiPermissionItem } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('role_management');

interface ApiPermissionGroup {
  controllerName: string;
  items: ApiPermissionItem[];
}

interface ApiPermissionTreeProps {
  items: ApiPermissionItem[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}

const ApiPermissionTree: React.FC<ApiPermissionTreeProps> = ({
  items,
  selectedIds,
  onChange,
  disabled,
}) => {
  const [expandedControllers, setExpandedControllers] = useState<Set<string>>(new Set());

  // Nhóm theo Controller cho dễ nhìn — permission chưa xác định controller rơi vào nhóm "Khác"
  const groups = useMemo<ApiPermissionGroup[]>(() => {
    const map = new Map<string, ApiPermissionItem[]>();
    items.forEach(item => {
      const key = item.controllerName || st('khac_text');
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(item);
    });
    return Array.from(map.entries())
      .map(([controllerName, groupItems]) => ({ controllerName, items: groupItems }))
      .sort((a, b) => a.controllerName.localeCompare(b.controllerName));
  }, [items]);

  const toggleExpanded = (controllerName: string) => {
    setExpandedControllers(prev => {
      const next = new Set(prev);
      if (next.has(controllerName)) {
        next.delete(controllerName);
      } else {
        next.add(controllerName);
      }
      return next;
    });
  };

  const handleToggleChild = (group: ApiPermissionGroup, id: number) => {
    const selected = new Set(selectedIds);
    if (selected.has(id)) {
      selected.delete(id);
    } else {
      selected.add(id);
    }
    onChange(Array.from(selected));
  };

  // Chọn/bỏ chọn node cha (Controller) -> áp dụng cho toàn bộ con trực tiếp bên trong.
  const handleToggleParent = (group: ApiPermissionGroup) => {
    const selected = new Set(selectedIds);
    const groupIds = group.items.map(item => item.id);
    const allSelected = groupIds.every(id => selected.has(id));

    if (allSelected) {
      groupIds.forEach(id => selected.delete(id));
    } else {
      groupIds.forEach(id => selected.add(id));
    }

    onChange(Array.from(selected));
  };

  // Chọn/bỏ chọn TẤT CẢ API permission (mọi controller) — cùng cơ chế check-toàn-phần với checkbox
  // cha từng nhóm ở trên, chỉ khác phạm vi là toàn bộ `items` thay vì 1 group.
  const allIds = useMemo(() => items.map(item => item.id), [items]);
  const allSelectedCount = allIds.filter(id => selectedIds.includes(id)).length;
  const isAllChecked = allIds.length > 0 && allSelectedCount === allIds.length;
  const isAllIndeterminate = allSelectedCount > 0 && !isAllChecked;

  const handleToggleAll = () => {
    onChange(isAllChecked ? [] : allIds);
  };

  if (groups.length === 0) {
    return <p className="text-muted mb-0">{st('chua_co_api_permission_nao_duoc_text')}</p>;
  }

  return (
    <div>
      <div
        className="border rounded px-3 py-2 mb-2"
        style={{
          backgroundColor: isAllChecked ? '#f0f9ff' : '#f8f9fa',
          borderColor: isAllChecked || isAllIndeterminate ? '#3b82f6' : '#e5e7eb',
        }}
      >
        <FormGroup check className="mb-0">
          <Input
            type="checkbox"
            id="api-permission-select-all"
            checked={isAllChecked}
            disabled={disabled}
            innerRef={(el: HTMLInputElement | null) => {
              if (el) el.indeterminate = isAllIndeterminate;
            }}
            onChange={handleToggleAll}
          />
          <Label
            check
            htmlFor="api-permission-select-all"
            className="ms-2"
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            <strong>{st('tat_ca_text')}</strong>
            <span className="text-muted small ms-2">
              ({allSelectedCount}/{allIds.length})
            </span>
          </Label>
        </FormGroup>
      </div>

      {groups.map(group => {
        const groupIds = group.items.map(item => item.id);
        const selectedCount = groupIds.filter(id => selectedIds.includes(id)).length;
        const isAllSelected = selectedCount === groupIds.length;
        const isIndeterminate = selectedCount > 0 && !isAllSelected;
        const isExpanded = expandedControllers.has(group.controllerName);

        return (
          <div key={group.controllerName}>
            <div
              className="border rounded px-3 py-2 mb-2"
              style={{
                backgroundColor: isAllSelected ? '#f0f9ff' : 'white',
                borderColor: isAllSelected || isIndeterminate ? '#3b82f6' : '#e5e7eb',
              }}
            >
              <div className="d-flex align-items-start">
                <Button
                  color="light"
                  size="sm"
                  className="btn-icon flex-shrink-0 me-2"
                  title={isExpanded ? st('thu_gon_text') : st('mo_rong_text')}
                  onClick={() => toggleExpanded(group.controllerName)}
                >
                  <i className={isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}></i>
                </Button>
                <FormGroup check className="mb-0 flex-grow-1">
                  <Input
                    type="checkbox"
                    id={`api-permission-controller-${group.controllerName}`}
                    checked={isAllSelected}
                    disabled={disabled}
                    innerRef={(el: HTMLInputElement | null) => {
                      if (el) el.indeterminate = isIndeterminate;
                    }}
                    onChange={() => handleToggleParent(group)}
                  />
                  <Label
                    check
                    htmlFor={`api-permission-controller-${group.controllerName}`}
                    className="d-flex align-items-start ms-2"
                    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                  >
                    <i className="ri-folder-3-line text-warning me-2 fs-5"></i>
                    <div className="flex-grow-1">
                      <strong className="d-block">
                        {group.controllerName}
                        {isIndeterminate && (
                          <span className="text-primary ms-2 small">{st('da_chon_1_phan_text')}</span>
                        )}
                      </strong>
                      <small className="text-muted">{group.items.length} {st('api_hint')}</small>
                    </div>
                  </Label>
                </FormGroup>
              </div>
            </div>

            {isExpanded &&
              group.items.map(item => {
                const isChecked = selectedIds.includes(item.id);
                return (
                  <div
                    key={item.id}
                    className="border rounded px-3 py-2 mb-2"
                    style={{
                      marginLeft: 24,
                      backgroundColor: isChecked ? '#f0f9ff' : 'white',
                      borderColor: isChecked ? '#3b82f6' : '#e5e7eb',
                    }}
                  >
                    <FormGroup check className="mb-0">
                      <Input
                        type="checkbox"
                        id={`api-permission-${item.id}`}
                        checked={isChecked}
                        disabled={disabled}
                        onChange={() => handleToggleChild(group, item.id)}
                      />
                      <Label
                        check
                        htmlFor={`api-permission-${item.id}`}
                        className="ms-2"
                        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                      >
                        <i className="ri-file-list-3-line text-muted me-2"></i>
                        {item.name}
                        <span className="text-muted small d-block">{item.code}</span>
                      </Label>
                    </FormGroup>
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
};

export default ApiPermissionTree;
